import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, Send, Sparkles, X, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";

// Inline mark — no external asset fetch, so the launcher renders offline.
const OperatorLogo = ({ className = "h-6 w-6" }) => (
  <span
    className={`${className} inline-flex items-center justify-center rounded-full bg-gradient-to-br from-primary/80 to-primary/40 font-bold text-background select-none`}
    aria-label="OMNΛS"
  >
    Λ
  </span>
);

/**
 * Operational quick questions, each answered by a deterministic backend
 * workflow — not generated prose. Free-typed questions go through the
 * operator command router instead.
 */
const QUICK_QUESTIONS = [
  { label: "What can start today?", workflow: "schedule_lookahead" },
  { label: "What is blocked / at risk?", workflow: "daily_risk_summary" },
  { label: "What decisions need me?", workflow: "approval_aging" },
  { label: "Procurement watchlist", workflow: "procurement_watchlist" },
  { label: "Bid deadlines", workflow: "bid_deadline_reminders" },
];

const OperatorAssistant = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [message, setMessage] = useState("");
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);

  const activeSessionId = activeSession?.id;

  const sortedMessages = useMemo(() => {
    const persisted = activeSession?.messages || [];
    const persistedIds = new Set(persisted.map((item) => item.id));
    return [...persisted, ...optimisticMessages.filter((item) => !persistedIds.has(item.id))];
  }, [activeSession, optimisticMessages]);

  const loadSessions = async () => {
    const response = await axios.get(`${API}/operator/sessions`);
    setSessions(response.data || []);
    return response.data || [];
  };

  const loadSession = async (sessionId) => {
    if (!sessionId) return null;
    const response = await axios.get(`${API}/operator/sessions/${sessionId}`);
    setActiveSession(response.data);
    setOptimisticMessages([]);
    return response.data;
  };

  /** Bind a new session to the screen the user is on, so answers are scoped. */
  const sessionContext = () => {
    const projectMatch = location.pathname.match(/^\/projects\/([^/]+)/);
    if (projectMatch) {
      return { context_type: "project", project_id: projectMatch[1], title: "Project assistant" };
    }
    if (location.pathname.startsWith("/procurement")) {
      return { context_type: "procurement", title: "Procurement assistant" };
    }
    if (location.pathname.startsWith("/schedule") || location.pathname.startsWith("/control-tower")) {
      return { context_type: "schedule", title: "Schedule assistant" };
    }
    return { context_type: "global", title: "Operator Assistant" };
  };

  const startNewSession = async () => {
    const response = await axios.post(`${API}/operator/sessions`, sessionContext());
    await loadSessions();
    await loadSession(response.data.id);
    return response.data.id;
  };

  const ensureSession = async () => {
    if (activeSessionId) return activeSessionId;
    const available = sessions.length ? sessions : await loadSessions();
    if (available.length > 0) {
      await loadSession(available[0].id);
      return available[0].id;
    }
    return startNewSession();
  };

  const loadWorkflows = async () => {
    const response = await axios.get(`${API}/operator/workflows`);
    setWorkflows(response.data.workflows || []);
  };

  useEffect(() => {
    if (!open) return;
    const init = async () => {
      try {
        const available = await loadSessions();
        await loadWorkflows();
        if (available.length > 0) {
          await loadSession(available[0].id);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Could not load Operator");
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [sortedMessages.length, open]);

  const sendMessage = async () => {
    const outgoing = message.trim();
    if (!outgoing) return;
    const tempId = `optimistic-${Date.now()}`;
    setMessage("");
    setOptimisticMessages((items) => [
      ...items,
      { id: tempId, role: "user", content: outgoing, created_at: new Date().toISOString(), optimistic: true },
    ]);
    setThinking(true);
    setLoading(true);
    try {
      const sessionId = await ensureSession();
      await axios.post(`${API}/operator/sessions/${sessionId}/commands`, {
        message: outgoing,
      });
      await loadSession(sessionId);
      await loadSessions();
    } catch (error) {
      setOptimisticMessages((items) => items.filter((item) => item.id !== tempId));
      setMessage(outgoing);
      toast.error(error.response?.data?.detail || "Could not run OMNΛS command");
    } finally {
      setThinking(false);
      setLoading(false);
    }
  };

  const runWorkflow = async (workflowName) => {
    setLoading(true);
    try {
      const sessionId = await ensureSession();
      await axios.post(`${API}/operator/workflows/${workflowName}/run`, {
        session_id: sessionId,
        persist_message: true,
      });
      await loadSession(sessionId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not run workflow");
    } finally {
      setLoading(false);
    }
  };

  /** Quick question: deterministic workflow when it exists, command otherwise. */
  const askQuickQuestion = async (question) => {
    const known = workflows.some((w) => w.name === question.workflow);
    if (known) {
      await runWorkflow(question.workflow);
      return;
    }
    setMessage(question.label);
  };

  const decideAction = async (actionId, decision) => {
    setLoading(true);
    try {
      const endpoint = decision === "approve" ? "approve" : "deny";
      await axios.post(`${API}/operator/actions/${actionId}/${endpoint}`);
      await loadSession(activeSessionId);
      toast.success(`Action ${decision === "approve" ? "confirmed" : "denied"}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${decision} action`);
    } finally {
      setLoading(false);
    }
  };

  const pendingActions = (activeSession?.actions || []).filter((action) => action.status === "needs_confirmation");
  const userFirstName = user?.full_name?.split(" ")?.[0] || user?.email?.split("@")?.[0] || "You";
  const roleLabel = (role) => (role === "user" ? userFirstName : role === "assistant" ? "OMNΛS" : role);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[70] h-14 w-14 rounded-full border border-white/15 bg-[#0F1016] p-0 shadow-2xl hover:bg-[#171924]"
        data-testid="operator-assistant-button"
        aria-label="Open OMNΛS assistant"
      >
        <OperatorLogo className="h-8 w-8" />
        {pendingActions.length > 0 && <Badge className="absolute -right-1 -top-1 h-5 min-w-5 bg-amber-500 px-1 text-[10px] text-black">{pendingActions.length}</Badge>}
      </Button>

      {open && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-black/25" onClick={() => setOpen(false)}>
          <div
            className="flex h-full w-full max-w-xl flex-col border-l bg-background shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            data-testid="operator-assistant-panel"
          >
            <div className="flex items-center justify-between border-b p-4">
              <div>
                <div className="flex items-center gap-2 font-semibold"><OperatorLogo className="h-7 w-7" /> OMNΛS</div>
                <p className="text-xs text-muted-foreground">
                  Answers come from OMNΛS system data. When the system doesn't know, it says so.
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  title="Start a new conversation scoped to this screen"
                  onClick={() => startNewSession().catch(() => toast.error("Could not start a conversation"))}
                >
                  <MessageSquarePlus className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="border-b p-3">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {QUICK_QUESTIONS.map((question) => (
                  <Button key={question.label} variant="outline" size="sm" disabled={loading} onClick={() => askQuickQuestion(question)}>
                    <Sparkles className="mr-1 h-3 w-3" /> {question.label}
                  </Button>
                ))}
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div ref={scrollRef} className="h-[calc(100vh-190px)] overflow-y-auto p-4 space-y-3">
                {!activeSession && (
                  <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                    Ask OMNΛS to look up records, make authorized changes, or run workflow summaries.
                  </div>
                )}

                {sortedMessages.map((item) => (
                  <div key={item.id} className={`rounded-lg border p-3 text-sm ${item.role === "user" ? "ml-10 bg-primary/10" : "mr-10 bg-muted/30"}`}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <Badge variant={item.role === "user" ? "default" : "secondary"}>{roleLabel(item.role)}</Badge>
                      <span className="text-[10px] text-muted-foreground">{item.created_at ? new Date(item.created_at).toLocaleString() : ""}</span>
                    </div>
                    <div className="whitespace-pre-wrap">{item.content}</div>
                  </div>
                ))}

                {thinking && (
                  <div className="mr-10 flex items-center gap-2 rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                    <OperatorLogo className="h-5 w-5" />
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>OMNΛS is thinking…</span>
                  </div>
                )}

                {pendingActions.map((action) => (
                  <div key={action.id} className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                    <div className="mb-1 flex items-center gap-2">
                      <Badge variant="secondary">needs confirmation</Badge>
                      <span className="font-medium">{action.tool_name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {action.result_json?.preview?.summary || "Review this guarded action before execution."}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" disabled={loading} onClick={() => decideAction(action.id, "approve")}>Confirm</Button>
                      <Button size="sm" variant="outline" disabled={loading} onClick={() => decideAction(action.id, "deny")}>Deny</Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t p-4">
              <div className="flex gap-2">
                <Input
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Ask OMNΛS anything..."
                  onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                  disabled={thinking}
                  data-testid="operator-assistant-input"
                />
                <Button onClick={sendMessage} disabled={thinking || !message.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OperatorAssistant;
